var __assign = (this && this.__assign) || function () {
    __assign = Object.assign || function(t) {
        for (var s, i = 1, n = arguments.length; i < n; i++) {
            s = arguments[i];
            for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p))
                t[p] = s[p];
        }
        return t;
    };
    return __assign.apply(this, arguments);
};
import { AwsUi } from '../../../core/runtime/src';
import { S3InContext } from './parts/s3-in-context';
import { S3Modal } from './parts/s3-modal';
import { S3Selection } from './parts/s3-selection';
import { PAGE_SIZES } from './helpers/s3-config';
import { browse, selectionChange, modalSubmit, modalDismiss, refresh, versionChange, handleNavigate, handleDrilldown, fireUpdate, uriChange, initInternalState, makeInitialFetchCall, getInContextProps, getModalProps, getSelectionProps, filteringChange, sortingChange } from './controllers/s3-resource-selector-controller';
export var S3ResourceSelector = AwsUi.__addComponent('awsui-s3-resource-selector', {
    releaseStatus: 'stable',
    properties: {
        // An array of the item types that are selectable in the table view. The array may contain the following items:
        // 'buckets', 'objects', or 'versions'. Example: `['buckets', 'objects']`. By default, no items are selectable.
        //
        // This property determines whether the component operates in Read mode or Write mode:
        //
        // * Read mode - When 'objects' and 'versions' values are provided (folder selection should be disabled by customizing `objectsIsItemDisabled` function)
        // * Write mode - When 'buckets' and 'objects' values are provided (file selection should be disabled by customizing `objectsIsItemDisabled` function)
        selectableItemsTypes: {
            type: 'array',
            itemTypes: ['string']
        },
        // The current selected resource. Resource has the following properties:
        //
        // * `uri` [string]: Optional - URI string of the resource path
        // * `versionId` [string]: Optional - Version ID of the selected resource
        resource: {
            type: 'object',
            objectType: 'S3ResourceSelector.Resource'
        },
        // Renders the component in a loading state.
        loading: {
            type: 'boolean'
        },
        // Type of the items currently being displayed.
        currentItemsType: {
            type: 'string',
            valid: ['buckets', 'objects', 'versions']
        },
        // List of the buckets. Each bucket has the following properties:
        //
        // * `ID` [string]: Unique identifier of the bucket
        // * `Name` [string]: Name of the bucket
        // * `CreationDate` [string]: Optional - Creation date of the bucket
        // * `Region` [string]: Optional - Region of the bucket
        // * `Access` [string]: Optional - Access type of the bucket
        buckets: {
            type: 'array',
            itemTypes: ['S3ResourceSelector.S3Bucket']
        },
        // List of the objects. Each object has the following properties:
        //
        // * `ID` [string]: Unique identifier of the object
        // * `Key` [string]: Name of the object
        // * `Size` [number]: Optional - Size of the object in bytes
        // * `BucketName` [string]: Optional - Name of the parent bucket
        // * `IsFolder` [boolean]: Determines whether an object is a folder or a file
        // * `LastModified` [string]: Optional - Date when the bucket was last modified
        // * `IsDeleteMarker` [boolean]: Optional - Determines whether the object is marked as deleted or not
        objects: {
            type: 'array',
            itemTypes: ['S3ResourceSelector.S3Object']
        },
        // List of the versions. Each version has the following properties:
        //
        // * `ID` [string]: Unique identifier of the version
        // * `CreationDate` [string]: Optional - Creation date of the version
        // * `Size` [number]: Optional - Size of the version in bytes
        // * `IsDeleteMarker` [boolean]: Optional - Determines whether the version is marked as deleted or not
        versions: {
            type: 'array',
            itemTypes: ['S3ResourceSelector.S3Version']
        },
        // Href of the selected object that is applied to the **View** button.
        viewHref: {
            type: 'string'
        },
        // Whether the URI input field is in invalid state
        invalid: {
            type: 'boolean'
        },
        // An object containing an alert message that must be displayed in the component. The object has the following properties:
        //
        // * `header` [string]: Header of the alert message
        // * `content` [string]: Content of the alert message
        // * `type` [string]: Type of the alert message. Supported values: 'success, 'error', 'warning', or 'info'
        alert: {
            type: 'object',
            objectType: 'S3ResourceSelector.Alert'
        },
        // Optionally overrides the set of visible columns in the **Buckets** view. Default: `['Name', 'Region', 'CreationDate']`. Available columns: 'ID', 'Name', 'CreationDate', 'Region', and 'Access'
        bucketsVisibleColumns: {
            type: 'array',
            itemTypes: ['string']
        },
        // Optionally overrides the set of visible columns in the **Objects** view. Default: `['Key', 'LastModified', 'Size']`. Available columns: 'ID', 'Key', 'LastModified', and 'Size'
        objectsVisibleColumns: {
            type: 'array',
            itemTypes: ['string']
        },
        // Optionally overrides the set of visible columns in the **Versions** view. Default: `['ID', 'CreationDate', 'Size']`. Available columns: 'ID', 'CreationDate', and 'Size'
        versionsVisibleColumns: {
            type: 'array',
            itemTypes: ['string']
        },
        // Optionally overrides whether a bucket should be disabled for selection in the Table view or not. It has higher priority than `selectableItemsTypes`. Example: if `selectableItemsTypes` has `['buckets']` value and `bucketsIsItemDisabled` returns `false` for a bucket, then the bucket is disabled for selection.
        bucketsIsItemDisabled: {
            type: 'function',
            functionType: 'TableSelection.disablingFunction',
            functionGenericType: 'S3ResourceSelector.S3Bucket'
        },
        // Optionally overrides whether an object should be disabled for selection in the Table view or not. Similar to `bucketsIsItemDisabled` this property takes precedence over the `selectableItemsTypes` property.
        objectsIsItemDisabled: {
            type: 'function',
            functionType: 'TableSelection.disablingFunction',
            functionGenericType: 'S3ResourceSelector.S3Object'
        },
        // Optionally overrides whether a version should be disabled for selection in the Table view or not. Similar to `bucketsIsItemDisabled` this property takes precedence over the `selectableItemsTypes` property.
        // By default, a version is disabled for selection if `IsDeleteMarker` of the version object is set to `true`.
        versionsIsItemDisabled: {
            type: 'function',
            functionType: 'TableSelection.disablingFunction',
            functionGenericType: 'S3ResourceSelector.S3Version'
        },
        // Sets the number of pages externally (example: server-side pagination). When this property is defined,
        // the component will not automatically paginate items. All items will be displayed, so the `items` property should contain
        // only the content of the current page. Use the `awsui:paginationChange` event to load different pages based on users' demand.
        //
        // Only positive integer numbers are allowed. Zero is ignored.
        pagesCount: {
            type: 'integer'
        },
        // Index of the current table page. The first page has index 1.
        //
        // If the specified `currentPageIndex` is out of bounds (lower than 1 or greater than the
        // total page count) it will be automatically set to `1`.
        currentPageIndex: {
            type: 'integer'
        },
        // Sets the pagination variant. It can be either default (when setting it to
        // `false`) or open ended (when setting it to `true`).
        // Default pagination will navigate you through the items list, while the open end
        // variant will allow you to lazy-load your items because it always displays three dots before
        // the next page icon. The next page button will never be disabled. When the user clicks on it
        // but there are no more items to show, the `nextPageClick` event will be fired with
        // `requestedPageAvailable=false`. Refer to the original description of the [openEnd](/components/awsui-table-pagination/#properties) event.
        openEnd: {
            type: 'boolean'
        },
        // Adds aria-describedby on the URI input. IDs of the elements containing description for the field should be provided, such as
        // validation error string, format, or additional description
        inputAriaDescribedby: {
            type: 'string'
        },
        // An object containing all the necessary localized strings required by the component.
        i18nStrings: {
            type: 'object',
            objectType: 'S3ResourceSelector.I18nStrings'
        },
        __state: {
            type: 'object',
            objectType: 'S3ResourceSelector.State'
        }
    },
    events: {
        // Fired when the resource needs to be updated.
        // The event `detail` object contains `resource` that represents the full path of the selected resource and `errorText` that may contain a validation error.
        update: {
            bubbles: true,
            cancelable: false,
            detailType: 'S3ResourceSelector.UpdateDetail'
        },
        // Fired when the resource selection is changed.
        // The event `detail` object contains `resource` that represents the full path of the selected resource and `errorText` that may contain a validation error.
        change: {
            bubbles: true,
            cancelable: false,
            detailType: 'S3ResourceSelector.ChangeDetail'
        },
        // Fired when fetch action is required.
        // The event `detail` object contains `resource` that represents the full path of the navigated resource, `errorText` that may contain a validation error, and `itemsType` determined from the resource URI string
        fetch: {
            bubbles: true,
            cancelable: false,
            detailType: 'S3ResourceSelector.FetchDetail'
        },
        // Fired when **Browse** button is clicked.
        browse: {
            bubbles: true,
            cancelable: false
        },
        // Fired when alert is being dismissed.
        alertDismiss: {
            bubbles: true,
            cancelable: false
        },
        // Fired when modal is being dismissed.
        // The event `detail` contains the `reason`, which can be any of the following:
        // `['closeButton', 'overlay', 'keyboard']`.
        modalDismiss: {
            bubbles: true,
            cancelable: false,
            detailType: 'Modal.DismissDetail'
        },
        // Fired when modal is being submitted.
        // The event `detail` contains an item selected in the table view.
        modalSubmit: {
            bubbles: true,
            cancelable: false,
            detailType: 'S3ResourceSelector.ModalSubmitDetail'
        },
        // Fired when refresh button is clicked.
        refresh: {
            bubbles: true,
            cancelable: false
        },
        // Fired when a user interaction triggers a pagination change in the server-side mode.
        // The event `detail` contains the `currentPageIndex`, the `pagesCount`
        // and the `pageSize`. Refer to the original description of the [paginationChange](/components/awsui-table-pagination/#events) event.
        paginationChange: {
            bubbles: true,
            cancelable: false,
            detailType: 'TablePagination.PaginationChangeDetail'
        },
        // Fired when a user interaction triggers a filtering change.
        // The event `detail` contains the current `filteringText`. Refer to the original description of the [filteringChange](/components/awsui-table-filtering/#events) event.
        filteringChange: {
            bubbles: true,
            cancelable: false,
            detailType: 'TableFiltering.FilteringChangeDetail'
        },
        // Fired when a user interaction triggers a sorting change.
        // The event `detail` contains the current `sortingColumn` and `sortingDescending`.
        // Refer to the original description of the [sortingChange](/components/awsui-table-sorting/#events) event.
        sortingChange: {
            bubbles: true,
            cancelable: false,
            detailType: 'TableSorting.SortingChangeDetail'
        }
    },
    callbacks: {
        initialized: function () {
            this.__state = {
                isModalOpen: false,
                selectedItems: [],
                validationErrorStrings: null,
                filteringText: '',
                sortingColumn: '',
                sortingDescending: false
            };
            this.__fireEvent = this.__fireEvent.bind(this);
            this.__drilldown = this.__drilldown.bind(this);
            this.__onNavigate = this.__onNavigate.bind(this);
            this.__onBrowse = this.__onBrowse.bind(this);
            this.__onModalDismiss = this.__onModalDismiss.bind(this);
            this.__onModalSubmit = this.__onModalSubmit.bind(this);
            this.__onRefresh = this.__onRefresh.bind(this);
            this.__onSelectionChange = this.__onSelectionChange.bind(this);
            this.__onVersionChange = this.__onVersionChange.bind(this);
            this.__onUriInput = this.__onUriInput.bind(this);
            this.__onUriChange = this.__onUriChange.bind(this);
            this.__onAlertDismiss = this.__onAlertDismiss.bind(this);
            this.__onPaginationChange = this.__onPaginationChange.bind(this);
            this.__onFilteringChange = this.__onFilteringChange.bind(this);
            this.__onSortingChange = this.__onSortingChange.bind(this);
        }
    },
    functions: {
        __onModalSubmit: function () {
            this.__state = modalSubmit(this);
        },
        __onModalDismiss: function (_a) {
            var detail = _a.detail;
            this.__state = modalDismiss(this, detail);
        },
        __onBrowse: function () {
            this.__state = browse(this);
        },
        __onSelectionChange: function (_a) {
            var detail = _a.detail;
            this.__state = selectionChange(this, detail);
        },
        __onNavigate: function (_a) {
            var detail = _a.detail;
            this.__state = handleNavigate(this, detail);
        },
        __drilldown: function (item) {
            this.__state = handleDrilldown(this, item);
        },
        __onRefresh: function () {
            refresh(this);
        },
        __onVersionChange: function (_a) {
            var detail = _a.detail;
            versionChange(this, detail);
        },
        __onUriChange: function (_a) {
            var detail = _a.detail;
            uriChange(this, detail);
        },
        __onUriInput: function (_a) {
            var detail = _a.detail;
            fireUpdate(this, { uri: detail.value });
        },
        __onAlertDismiss: function () {
            this.__fireEvent('alertDismiss');
        },
        __onPaginationChange: function (_a) {
            var detail = _a.detail;
            this.__fireEvent('paginationChange', detail);
        },
        __onFilteringChange: function (_a) {
            var detail = _a.detail;
            this.__state = filteringChange(this, detail);
        },
        __onSortingChange: function (_a) {
            var detail = _a.detail;
            this.__state = sortingChange(this, detail);
        }
    },
    render: function () {
        var _a = this, loading = _a.loading, invalid = _a.invalid, alert = _a.alert, viewHref = _a.viewHref, i18nStrings = _a.i18nStrings, currentPageIndex = _a.currentPageIndex, pagesCount = _a.pagesCount, openEnd = _a.openEnd, inputAriaDescribedby = _a.inputAriaDescribedby, __state = _a.__state;
        if (!i18nStrings) {
            return null;
        }
        if (this.__state.validationErrorStrings === null) {
            this.__state = initInternalState(this);
            makeInitialFetchCall(this);
        }
        var _b = getInContextProps(this), uri = _b.uri, showVersionSelector = _b.showVersionSelector, options = _b.options, selectedVersionId = _b.selectedVersionId;
        var s3InContextProps = {
            loading: loading,
            invalid: invalid,
            uri: uri,
            showVersionSelector: showVersionSelector,
            options: options,
            selectedVersionId: selectedVersionId,
            viewHref: viewHref,
            alert: alert,
            inputAriaDescribedby: inputAriaDescribedby,
            onVersionChange: this.__onVersionChange,
            onUriInput: this.__onUriInput,
            onUriChange: this.__onUriChange,
            onBrowse: this.__onBrowse,
            onAlertDismiss: this.__onAlertDismiss,
            i18nStrings: i18nStrings
        };
        var _c = getSelectionProps(this), itemsType = _c.itemsType, columnDefinitions = _c.columnDefinitions, sortableColumns = _c.sortableColumns, isItemDisabled = _c.isItemDisabled, items = _c.items, visibleColumns = _c.visibleColumns, filteringLabel = _c.filteringLabel;
        var s3SelectionProps = {
            loading: loading,
            itemsType: itemsType,
            columnDefinitions: columnDefinitions,
            sortableColumns: sortableColumns,
            isItemDisabled: isItemDisabled,
            items: items,
            selectedItems: __state.selectedItems,
            visibleColumns: visibleColumns,
            pageSize: PAGE_SIZES[itemsType],
            pagesCount: pagesCount,
            currentPageIndex: currentPageIndex,
            openEnd: openEnd,
            filteringText: __state.filteringText,
            filteringLabel: filteringLabel,
            sortingColumn: __state.sortingColumn,
            sortingDescending: __state.sortingDescending,
            onPaginationChange: this.__onPaginationChange,
            onFilteringChange: this.__onFilteringChange,
            onSortingChange: this.__onSortingChange,
            onSelectionChange: this.__onSelectionChange,
            onRefresh: this.__onRefresh,
            i18nStrings: i18nStrings
        };
        var _d = getModalProps(this), breadcrumbs = _d.breadcrumbs, isSubmitEnabled = _d.isSubmitEnabled;
        var s3ModalProps = {
            visible: __state.isModalOpen,
            isSubmitEnabled: isSubmitEnabled,
            breadcrumbs: breadcrumbs,
            alert: alert,
            onNavigate: this.__onNavigate,
            onSubmit: this.__onModalSubmit,
            onModalDismiss: this.__onModalDismiss,
            onAlertDismiss: this.__onAlertDismiss,
            i18nStrings: i18nStrings
        };
        return (AwsUi.__createElement("div", { className: "awsui-s3-resource-selector-container" },
            AwsUi.__createElement(S3InContext, __assign({}, s3InContextProps)),
            AwsUi.__createElement(S3Modal, __assign({}, s3ModalProps, { content: AwsUi.__createElement(S3Selection, __assign({}, s3SelectionProps)) }))));
    }
});
//# sourceMappingURL=s3-resource-selector.js.map